import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { fetchUserImage } from "../src/api";
import { BackendURL } from "../env";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isAuthorizationReady, setIsAuthorizationReady] = useState(false);

  // ---------------------------
  // 1. KEEP ALIVE (every 1 min)
  // ---------------------------
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${BackendURL}/keep-alive`, {
          credentials: "include",
        });

        if (!res.ok) {
          localStorage.clear();
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error("Keep-alive failed", err);
        localStorage.clear();
        setIsAuthenticated(false);
      }
    }, 1 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // ---------------------------
  // 2. CHECK SESSION ON LOAD
  // ---------------------------
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await fetch(`${BackendURL}/session-user`, {
          credentials: "include",
        });

        if (!res.ok) {
          localStorage.clear();
          setIsAuthenticated(false);
          return;
        }

        const data = await res.json();
        setIsAuthenticated(true);
        setIsAuthorizationReady(false);

        localStorage.setItem("Email", data.email || "");
        localStorage.setItem("jobTitle", data.jobTitle || "");
        localStorage.setItem("name", data.name || "");
        localStorage.setItem("Entraid", data.id || "");
        localStorage.setItem("accesstoken", data.access_token || "");
      } catch (err) {
        console.error("Session check failed:", err);
        localStorage.clear();
        setIsAuthenticated(false);
        setIsAuthorizationReady(false);
      }
    };

    checkSession();
  }, []);

  // ---------------------------
  // 3. CONFIRM USER EXISTS IN DB
  // ---------------------------
  useEffect(() => {
    const checkUserInDb = async () => {
      const email = localStorage.getItem("Email");


      if (!email) {
        setIsAuthenticated(false);
        setIsAuthorizationReady(false);
        return;
      }

      try {
        const response = await fetch("/api/Check_User_in_DB", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ Email: email }),
        });

        const data = await response.json().catch(() => null);
        const isPrysm =
          data?.Is_Prysm === true ||
          data?.Is_Prysm === 1 ||
          data?.Is_Prysm === "true";
        localStorage.setItem("PrysmView", isPrysm ? "1" : "0");

        if (!response.ok || data?.status !== "Success") {
          console.error("User validation failed:", data);
          localStorage.clear();
          setIsAuthenticated(false);
          setIsAuthorizationReady(false);
          return;
        }

        setIsAuthorizationReady(true);
      } catch (error) {
        console.error("DB user check failed:", error);
        localStorage.clear();
        setIsAuthenticated(false);
        setIsAuthorizationReady(false);
      }
    };

    if (isAuthenticated) {
      checkUserInDb();

    } else {
      setIsAuthorizationReady(false);
    }
  }, [isAuthenticated]);

  // ---------------------------
  // 4. PROFILE IMAGE LOAD
  // ---------------------------
  const [image, setImage] = useState("");

  useEffect(() => {
    const cached = localStorage.getItem("profileImage");
    if (cached) {
      setImage(cached);
      return;
    }

    fetchUserImage().then((img) => {
      if (img) {
        localStorage.setItem("profileImage", img);
        setImage(img);
      }
    });
  }, [isAuthenticated]);

  // ---------------------------
  // 5. BLOCK UNTIL AUTH CHECKED
  // ---------------------------
  if (isAuthenticated === null || (isAuthenticated && !isAuthorizationReady)) return null;

  if (!isAuthenticated) {
    // Old login-page redirect kept for reference:
    // return <Navigate to="/signin" replace />;
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;

